    import { Button } from '@ui/buttons/button';
  import { Trans } from '@ui/i18n/trans';
  import { Form } from '@ui/forms/form';
  import { Fragment, useEffect, useRef, useState, useMemo, ReactNode } from 'react';
  import { DefaultMetaTags } from '@common/seo/default-meta-tags';
  import { Helmet } from '@common/seo/helmet';
  import { CheckCircleOutlineIcon } from '@ui/icons/material/CheckCircleOutline';
  import { ArrowLeftIcon } from '@ui/icons/material/ArrowLeftIcon';
  import { ArrowRightIcon } from '@ui/icons/material/ArrowRightIcon';
  import { BillingCycleRadio } from '@common/billing/pricing-table/billing-cycle-radio';
  import { PricingTable } from '@common/billing/pricing-table/pricing-table';
  import { UpsellBillingCycle } from '@common/billing/pricing-table/find-best-price';
  import { useProducts } from '@common/billing/pricing-table/use-products';
  import { FacebookIcon } from '@ui/icons/social/facebook';
  import { InstagramIcon } from '@ui/icons/social/instagram';
  import { YoutubeIcon } from '@ui/icons/social/youtube';
  import {
    ContactPagePayload,
    useSubmitContactForm,
  } from '@common/contact/use-submit-contact-form';
  import { useForm } from 'react-hook-form';
  import { Dialog } from '@ui/overlays/dialog/dialog';
  import { DialogBody } from '@ui/overlays/dialog/dialog-body';
  import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
  import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
  import { FormTextField } from '@ui/forms/input-field/text-field/text-field';
  import { useRecaptcha } from '@common/recaptcha/use-recaptcha';
  import { LandingNavbar } from '@app/landing/landing-navbar';
  import '@app/landing/landing-page.css';
  import { FullPageLoader } from '@ui/progress/full-page-loader';
  import { LucideIcon } from "lucide-react";
  // import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt  } from "react-icons/fa";
  import { motion } from "framer-motion";
  import { Shield, Lock, Database,BookOpen , FolderKanban, FileCheck, Users, Building2 , GraduationCap , Gauge, UserCog, RefreshCw, BarChart3, UserPlus, Upload, Globe2 } from 'lucide-react';
  import { ArrowRight, Sparkles } from 'lucide-react';
  import { useThemeSelector } from '@ui/themes/theme-selector-context';

  export function Education() {
    const query = useProducts('pricingPage');
    const [selectedCycle, setSelectedCycle] =
      useState<UpsellBillingCycle>('month');

    const { selectThemeTemporarily } = useThemeSelector();
    useEffect(() => {
      selectThemeTemporarily('light');
      return () => selectThemeTemporarily(null);
    }, [selectThemeTemporarily]);

    useEffect(() => {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }, []);

    interface Card {
        icon: LucideIcon;
        title: string;
        description: ReactNode;
      }
        const [isMobile, setIsMobile] = useState<boolean>(false);
        const [hoverIndex, setHoverIndex] = useState<number | null>(null);
      
        useEffect(() => {
          const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
          };
      
          handleResize();
          window.addEventListener("resize", handleResize);
          return () => window.removeEventListener("resize", handleResize);
        }, []);
      
        const card: Card[] = [
        {
          icon: BookOpen ,
          title: "Students",
          description: "Students handle many files like assignments and projects. Space Byte keeps their work secure and accessible from any device.",
        },
        {
          icon: GraduationCap   ,
          title: "Educators",
          description: "Teachers and educators can store lecture notes, presentations, PDFs, and learning resources in one place to easily share with learners.",
        },
        {
          icon: Building2  ,
          title: "Training Institutes",
          description: "Institutes offering digital courses or training programs can store materials, resources, and recorded sessions without storage concerns.",
        },
        {
          icon: Users ,
          title: "Academic Teams",
          description: (
    <>
      Research involves large datasets and documents. {" "}
      <a
        href="/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0072ff] font-bold hover:underline"
      >
        SpaceByte cloud storage,
      </a>{" "}
       protecting content if devices fail.
    </>
  ),
        },
      ];

    const benefit = [
                {
                  title: "Secure Cloud Storage",
                  description: "Academic documents, learning resources, and institutional files stay protected with advanced security systems.",
                  icon: Lock,
                },
                {
                  title: "Access Files Anywhere",
                  description: "Students and educators can access their files from laptops, tablets, or smartphones whenever they need them.",
                  icon: Globe2,
                },
                {
                  title: "Easy Collaboration",
                  description: "Sharing files between students, educators, and teams becomes simple and fast.",
                  icon: Users,
                },
                {
                  title: "Organized Digital Workspace",
                  description: "All learning materials stay neatly organized so finding the right file takes seconds instead of minutes.",
                  icon: Database,
                },
                {
                  title: "Flexible Storage for Growing Needs",
                  description: "As digital content grows, institutions can scale storage without changing their entire system.",
                  icon: RefreshCw,
                },
                
                
              ]

    const steps = [
      {
        icon: UserPlus,
        number: '01',
        title: 'Sign Up',
        description: 'Create your account in seconds with no credit card required',
      },
      {
        icon: Upload,
        number: '02',
        title: 'Upload Files',
        description: 'Upload files or sync folders instantly with secure, high-speed cloud storage',
      },
      {
        icon: Globe2,
        number: '03',
        title: 'Access Anywhere',
        description: 'Access your data from any device, anywhere in the world',
      },
    ];

    

    const keybenefit = [
    
                  { 
                    number: 1,
                    title: "Centralized storage for educational files",
                    icon: Database
                  },
                  { 
                    number: 2,
                    title: "Secure backup for academic data",
                    icon: Lock
                  },
                  { 
                    number: 3,
                    title: "Easy file sharing between learners and educators",
                    icon: Users
                  },
                  { 
                    number: 4,
                    title: "Access study materials from anywhere",
                    icon: Globe2
                  },
                  { 
                    number: 5,
                    title: "Organize learning resources more efficiently",
                    icon: FileCheck
                  },
                  { 
                    number: 6,
                    title: "Reliable cloud storage service for education",
                    icon: RefreshCw
                  }
    ]

    const trackRef = useRef<HTMLDivElement | null>(null);
    const [paused, setPaused] = useState(false);

    const logos = [
      { src: "/images/LandingImg/theweek.png", name: "theweek" },
      { src: "/images/LandingImg/pti.jpeg", name: "pti" },
      { src: "/images/LandingImg/loktejenglish.png", name: "loktejenglish" },
      { src: "/images/LandingImg/samachar.png", name: "samachar" },
      { src: "/images/LandingImg/up18new.webp", name: "up18new" },
      { src: "/images/LandingImg/wow.png", name: "wow" },
      { src: "/images/LandingImg/lusakatimes.webp", name: "lusakatimes" },
    ];

    // duplicate for infinite scroll
    const items = [...logos, ...logos];

    useEffect(() => {
      const track = trackRef.current;
      if (!track) return;

      let raf = 0;
      let x = 0;

      const speed = 0.65;

      const animate = () => {
        if (!paused) {
          x -= speed;

          const halfWidth = track.scrollWidth / 2;
          if (Math.abs(x) >= halfWidth) x = 0;

          track.style.transform = `translateX(${x}px)`;
        }

        raf = requestAnimationFrame(animate);
      };

      raf = requestAnimationFrame(animate);

      return () => cancelAnimationFrame(raf);
    }, [paused]);

    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const toggle = (index: number) => {
      setOpenIndex(openIndex === index ? null : index);
    };

    const faqData = [
      {
        question: "What is cloud storage for education?",
        answer:
          "Cloud storage for education allows students, teachers, and institutions to securely store, organize, and access academic files from any device.",
      },
      {
        question: "Can students and teachers access files from anywhere?",
        answer:
          "Yes. Cloud storage allows users to access files from any location with an internet connection, making it easier to support remote learning and digital education.",
      },
      {
        question: "What happens if I accidentally delete a file?",
        answer:
          "Space Byte cloud storage systems include backup or recovery options to help restore deleted files and prevent permanent data loss.",
      },
      {
        question: "How much storage does Space Byte provide?",
        answer:
          "Space Byte offers flexible cloud storage plans so students, educators, and institutions can choose storage based on their needs.",
      },
    ];

    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    const reviews = useMemo(
      () => [
        {
          name: "Amit Sharma",
          role: " College Lecturer",
          review: "Space Byte helps me keep lecture notes and study materials organized. It’s simple to use and makes sharing files with students easier.",
          avatar: "",
          rating: 5,
          accent: "#2563EB",
          light: "#EAF2FF",
        },
        {
          name: "Anjali Desai",
          role: "University Student",
          review: "I use Space Byte to store assignments and project files. It’s helpful to access everything from any device.",
          avatar: "",
          rating: 4.2,
          accent: "#7C3AED",
          light: "#F3E8FF",
        },
        {
          name: "Nikita Joshi",
          role: "Postgraduate Student",
          review: "It’s useful for storing research papers, notes, and project files. Everything stays organized.",
          avatar: "",
          rating: 4,
          accent: "#0284C7",
          light: "#E0F2FE",
        },
      ],
      []
    );

  const total = reviews.length;

    useEffect(() => {
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) setVisible(true);
        },
        { threshold: 0.12 }
      );

      if (ref.current) obs.observe(ref.current);
      return () => obs.disconnect();
    }, []);

    const prev = () => setActiveIndex((p) => (p === 0 ? total - 1 : p - 1));
    const next = () => setActiveIndex((p) => (p === total - 1 ? 0 : p + 1));

  
    const cardWidth = 450; // desktop reference
    const gap = 24;

    // Offset only for desktop logic
    const offset = activeIndex * (cardWidth + gap);

    // const TawkTo = () => {
    //   useEffect(() => {
    //     const Tawk_API = (window as any).Tawk_API || {};
    //     const Tawk_LoadStart = new Date();

    //     const script = document.createElement('script');
    //     script.async = true;
    //     script.src = 'https://embed.tawk.to/679a04db3a8427326076ac96/1iioq5v0g';
    //     script.charset = 'UTF-8';
    //     script.setAttribute('crossorigin', '*');

    //     document.body.appendChild(script);

    //     return () => {
    //       document.body.removeChild(script);
    //     };
    //   }, []);

    //   return null; // This component doesn't render anything
    // };

      
    return (
      <Fragment>
          <Helmet>
          <title>Secure Cloud Storage for Education | SpaceByte</title>
          <meta
            name="description"
            content="Cloud storage for education to store, organize, and access academic files easily. Perfect for schools, teachers, and institutions managing digital resources."
          />
          <meta name="keywords" content="Cloud Storage for Education" />
          <link rel="canonical" href="https://spacebyte.in/cloud-storage-for-education" />
        </Helmet>

        <LandingNavbar
          color="transparent"
          className="fixed top-0 z-50 w-full flex-shrink-0 border-none bg-light-primary shadow-lg"
          menuPosition="homepage-navbar"
        />

      <section className=" relative overflow-hidden !bg-gradient-to-r !from-[#0072ff]/8 !to-[#00c6ff]/8 py-24 md:py-32">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1E3A8A" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div
          className={`landing-container z-10 flex flex-col items-center justify-between pb-40 pt-100 lg:flex-row`}
        >
          <div className="mx-auto w-full max-w-6xl px-4 text-center">

            {/* Heading */}
            <h1 className="font-archivo text-[40px] md:text-[60px] font-bold leading-[1.2] tracking-[-0.02em] text-[#1f2937]">
              Smart Cloud Storage {" "} 
              <br />  
              for  <span className="color-primary-gradient"> Digital Learning </span>
              <br />

            </h1>

            <p className="mx-auto mt-30 max-w-3xl font-inter text-[18px] md:text-[20px]  text-[#6b7280]">
              Support modern education, keep learning materials organized, and help students and educators access files anytime with Space Byte cloud storage service.
            </p>

          <div className="my-36 pb-20 flex flex-col items-center justify-center gap-6 sm:flex-row">
            <a
              href="/register"
              className="inline-flex items-center justify-center 
              px-26 py-10 
              text-white font-bold
              bg-gradient-to-r from-[#0072ff] to-[#00c6ff]
              shadow-lg transition-all duration-300
              hover:from-[#0059d6] hover:to-[#00aaff]
              hover:shadow-2xl hover:-translate-y-1"
              style={{
              borderRadius: "12px"
            }}
            >
              Get 25 GB FREE
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center justify-center
              px-36 py-8 font-bold
              text-[#0072ff]
              relative transition-all duration-300
              hover:-translate-y-1 hover:shadow-lg "
              style={{
                borderRadius: "12px",
                background: "linear-gradient(#e5f5fb, #e5f5fb) padding-box, linear-gradient(90deg, #0072ff, #00c6ff) border-box",
                border: "2px solid transparent"
              }}
              >
              View Pricing
            </a>
          </div>
          </div>
        </div>
      </section>   

      <section className="py-24 md:py-32 bg-gradient-to-br from-[#F8FAFC] to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 landing-container z-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#3B82F6] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#06B6D4] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 my-30 md:my-50 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827] mb-10">
              Start Your Cloud Storage in <span className="color-primary-gradient">Three Steps</span>{" "}

            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto pb-10 font-archivo">
              Start storing and sharing your files securely in minutes
            </p>
          </motion.div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative landing-container">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-90 left-0 right-0 h-2 bg-gradient-to-r from-[#3B82F6] via-[#60A5FA] to-[#06B6D4] opacity-30"></div>

            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="flex flex-col items-center text-center">
                  {/* Icon Circle */}
                  <div className="relative mb-6">
                    <div className="w-80 h-80 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center relative z-10 shadow-lg">
                      <step.icon className="w-40 h-40 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-10  w-40 h-40 bg-white rounded-full flex items-center justify-center border-2 border-[#3B82F6] z-20">
                      <span className="text-[#1E3A8A] font-bold text-sm">{step.number}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-semibold font-archivo text-[#111827] my-20">
                    {step.title}
                  </h3>
                  <p className="text-[#6B7280] font-archivo max-w-xs">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Animated Line */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, delay: 0.5 }}
            className=" hidden md:block absolute top-[260px] left-[25%] right-[25%] h-3 bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] rounded-full origin-left"
          ></motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#f5f8ff] py-40 text-black lg:py-50">
        <div className="landing-container z-10 flex flex-col items-center justify-between gap-8 px-4 md:px-8 lg:flex-row">
          {/* Left Section */}
          <div className="relative w-full lg:w-1/2 flex justify-center">
            <div className="relative w-full max-w-[520px]">

              {/* Background Offset Block */}
              <div className="absolute -bottom-4 -right-4 h-full w-full rounded-2xl bg-gradient-to-br from-blue-600 to-blue-400" />

              {/* Image */}
              <img
                src="/images/education/education.webp"
                alt="cloud storage for education"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>

          {/* Right Content */}
          <div className="w-full space-y-16 lg:w-1/2 pt-14 md:pt-0">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
             Secure Cloud Storage <span className="color-primary-gradient"> for Education </span>
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              Assignments, research, and lecture recordings create many digital files in modern education. Cloud storage for education, like Space Byte, keeps them secure, organized, and easy to access anytime.
            </p>
          </div>
        </div>
      </section>
      
      <section
        id="Pricing"
        className="pricing-section relative overflow-hidden bg-white py-40 text-black lg:py-50"
      >
        <div className="landing-container z-10">
          <div className="mx-auto max-w-xl space-y-16 text-center">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Affordable <span className="color-primary-gradient"> Cloud Storage Pricing </span> Plans
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              Get affordable pricing with no hidden fees, encrypted cloud storage,
              and fast, seamless access to your files anytime, anywhere.
            </p>
          </div>

          <div className="my-40 flex flex-col md:flex-row justify-center items-center gap-16">
            <BillingCycleRadio
              products={query.data?.products}
              selectedCycle={selectedCycle}
              onChange={setSelectedCycle}
              className="input-radio animate-fade-up flex justify-center items-center flex-wrap"
              size="lg"
            />
          </div>

          <PricingTable
            selectedCycle={selectedCycle}
            productLoader="pricingPage"
            className="animate-fade-down"
          />

          {/* Premium Section */}
          <div
            className="premium-bg animate-fade-down bg-light-primary relative mt-40 flex flex-col items-center justify-between space-y-8 overflow-hidden rounded-2xl border-primary p-16 shadow-lg md:flex-row md:space-y-0 md:p-32"
            style={{
              backgroundImage:
                "url('/images/LandingImg/ctamask.png'), url('/images/LandingImg/ctashape.png')",
              backgroundSize: 'contain, contain',
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundPosition: 'left center, right center',
            }}
          >
            {/* Premium Tag */}
            <div className="font-archivo absolute -left-54 top-16 w-160 rotate-[-45deg] transform rounded bg-warning text-center text-sm font-medium text-black md:-left-56 lg:-left-50 lg:top-20">
              Premium
            </div>

            {/* Icon and Text */}
            <div className="flex flex-col items-center gap-8 space-y-8 md:flex-row md:space-x-8 md:space-y-0">
              <img
                src="/images/LandingImg/cta-icon.webp"
                className="color-primary rounded-lg bg-white p-16"
              />
              <div className="text-center md:text-left">
                <h2 className="font-archivo text-3xl font-bold">
                  5 TB or More?
                </h2>
                <span className="color-primary font-archivo text-2xl font-semibold">
                  ₹0.15/GB{' '}
                  <span className="text-lg font-normal text-black">
                    /Monthly
                  </span>
                </span>
                <p className="font-inter mt-8 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> 100% Safe & Secure with
                  End-to-End Encryption
                </p>
                <p className="font-inter mt-1 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> Calculate the cost or
                  Reach out to us
                </p>
                <p className="font-inter mt-4 text-sm text-gray-700 max-w-md hidden md:block">
                  SpaceByte Cloud Storage is a scalable enterprise platform designed for massive data needs of 5 TB or more. Our secure infrastructure handles high-volume backups seamlessly and affordably, offering custom storage plans for heavy business workloads that guarantee zero-knowledge encryption and uninterrupted access.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-row flex-wrap gap-8 sm:flex-col lg:flex-row justify-center">
              <div className="flex justify-center">
                {CalculateDialogTrigger()}
              </div>

              <div className="mt-0 flex justify-center">
                <a href="/contact" className="btn bg-[#f9fafb] border-2 border-[#0072ff] text-black hover:bg-[#0072ff] hover:text-white">
                  <span>Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-20 md:py-32 bg-[#f5f8ff]">
        <div className="landing-container z-10 "
      style={{
        padding: "10px 10px",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: "70px",
          alignItems: "center",
        }}
      >
        {/* LEFT SIDE */}
        <div>
          <h2 className="animate-fade-down font-archivo mt-10 text-3xl font-bold md:text-4xl lg:text-5xl">
              Education with  
              <br />
                <span className="color-primary-gradient"> SpaceByte</span>
              
              </h2>
              <p className="color-gray font-inter animate-fade-down py-16 md:py-24">
                Education today goes beyond classrooms, involving students, educators, institutes, and digital platforms. Space Byte cloud storage helps them manage and access learning resources easily.
              </p>

        </div>

        {/* RIGHT SIDE */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "14px",
            marginTop: "14px",
          }}
        >
          {card.map((card, index) => (
            <div
              key={index}
              style={{
                background: "#e6ecfb",
                border: "1px solid #ddeaff",
                borderRadius: "14px",
                padding: "20px",
                transition: "all 0.25s ease",
                transform:
                  hoverIndex === index
                    ? "translateY(-3px)"
                    : "translateY(0px)",
                boxShadow:
                  hoverIndex === index
                    ? "0 8px 28px rgba(0,112,255,0.10)"
                    : "none",
                cursor: "pointer",
              }}
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <div style={{ fontSize: "22px", marginBottom: "8px" }}>
                <card.icon size={28} strokeWidth={2} className="text-[#0072ff]" />
              </div>

              <strong
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#0d0d2b",
                  marginBottom: "4px",
                }}
              >
                {card.title}
              </strong>

              <span
                style={{
                  fontSize: "12px",
                  color: "#7a8fa6",
                }}
              >
                {card.description}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
      </section>

      <section className="py-32 md:py-50 bg-gradient-to-r from-white via-[#3B82F6]/3 to-white relative overflow-hidden border-l-4 border-r-4 border-[#3B82F6]/30">
        <div className="absolute inset-0 opacity-4  ">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="edu-dots" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="4" fill="#3B82F6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#edu-dots)" />
          </svg>
        </div>
        <div className="landing-container max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-24 px-4 md:px-8"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827] mb-12">
              Modern <span className="color-primary-gradient">Learning Workflows</span>
            </h2>
            <p className="text-lg text-[#6B7280] max-w-3xl mx-auto font-inter leading-relaxed">
              Digital learning requires a system that keeps information structured and accessible. SpaceByte cloud storage helps simplify daily academic workflows.
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto px-4 md:px-0">
            {/* Center Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-[#3B82F6] to-[#06B6D4]" style={{top: '120px', bottom: 0}}></div>

            <div className="relative space-y-12">
              {[
                {
                  title: "Organize Study Materials Easily",
                  description: "Store notes, documents, PDFs, and educational resources in one place so they are always easy to find.",
                  icon: Database,
                  step: "01",
                  side: "left",
                },
                {
                  title: "Manage Assignments & Projects",
                  description: "Students can keep their assignments and projects securely stored without worrying about accidental deletion or device issues.",
                  icon: FileCheck,
                  step: "02",
                  side: "right",
                },
                {
                  title: "Store Lecture Recordings",
                  description: "Online lectures and training videos often require large storage space. Cloud storage helps keep these files safe and accessible anytime.",
                  icon: BarChart3,
                  step: "03",
                  side: "left",
                },
                {
                  title: "Share Files Quickly",
                  description: "Educators can easily share study materials and resources with learners without sending multiple files repeatedly.",
                  icon: Upload,
                  step: "04",
                  side: "right",
                },
                {
                  title: "Reliable Backup for Important Files",
                  description: "Important academic data remains protected with secure cloud storage and reliable backup.",
                  icon: Shield,
                  step: "05",
                  side: "left",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className={`flex items-center ${item.side === 'left' ? 'flex-row' : 'flex-row-reverse'} gap-6 md:gap-0`}
                >
                  {/* Content Box */}
                  <div className={`w-full md:w-1/2 ${item.side === 'left' ? 'md:pr-12 md:text-left' : 'md:pl-12 md:text-left'} px-4 md:px-0`}>
                    <motion.div
                      initial={{ opacity: 0, x: item.side === 'left' ? -30 : 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      className="bg-white rounded-xl p-20 shadow-lg hover:shadow-xl transition-all duration-300 border border-[#E2E8F0] hover:border-[#3B82F6]/30"
                    >
                      <h3 className="text-lg font-semibold font-archivo text-[#111827] mb-3">
                        {item.title}
                      </h3>
                      <p className="text-sm text-[#6B7280] font-inter leading-relaxed">
                        {item.description}
                      </p>
                    </motion.div>
                  </div>

                  {/* Center Circle with Number */}
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 + 0.1 }}
                    className="hidden md:flex items-center justify-center w-50 h-50 bg-gradient-to-br from-[#3B82F6] to-[#06B6D4] rounded-full shadow-lg flex-shrink-0 z-10 relative border-4 border-white"
                  >
                    <span className="text-2xl font-black text-white">
                      {item.step}
                    </span>
                  </motion.div>

                  {/* Empty Space for Right Side */}
                  <div className="w-full md:w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 md:py-50 bg-gradient-to-b from-[#0072ff]/6 via-[#00c6ff]/4 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-3 pointer-events-none">
        </div>

        <div className="landing-container z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-24 px-4 md:px-8"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827] mb-12">
              Why Education Professionals Choose <span className="color-primary-gradient">SpaceByte</span>
            </h2>
            <p className="text-lg text-[#6B7280] max-w-3xl mx-auto font-inter leading-relaxed">
Educational organizations need secure and reliable storage. Cloud storage for education from Space Byte helps store, organize, and access academic files easily.            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-16 px-4 md:px-0 text-center">
  {benefit.map((item, index) => (
    <motion.div
      key={index}
      className="w-[45%] lg:w-[30%] bg-white rounded-2xl p-14 shadow-lg hover:shadow-2xl transition-all duration-300 border border-[#E2E8F0] hover:border-[#3B82F6]"
    >
      <div className="inline-flex items-center justify-center w-50 h-50 bg-gradient-to-br from-[#3B82F6] to-[#06B6D4] rounded-full mb-8">
        <item.icon className="w-30 h-30 text-white" />
      </div>

      <h3 className="text-lg font-semibold text-[#111827] mb-3">
        {item.title}
      </h3>

      <p className="text-[#6B7280] leading-relaxed text-base">
        {item.description}
      </p>
    </motion.div>
  ))}
</div>
        </div>
      </section>
  
      <section className="py-30 md:py-50 bg-gradient-to-br from-[#F8FAFC] to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 landing-container z-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#3B82F6] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#06B6D4] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4  sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827] mb-50">
              Key Benefits for the <span className="color-primary-gradient">Education Sector</span>{" "}

            </h2>
          
          </motion.div>

          {/* Steps */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12 relative landing-container">
          
            {keybenefit.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative group"
              >
                <div className="flex flex-col items-center text-center">
                  {/* Icon Circle */}
                  <div className="relative mb-6">
                    <div className="w-60 h-60 rounded-full flex items-center justify-center relative z-10 shadow-lg bg-[#F3F4F6] group-hover:bg-[#3B82F6] transition-all duration-300">
                      <step.icon className="w-30 h-30 text-[#3B82F6] group-hover:text-white transition-all duration-300" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-medium font-archivo text-[#111827] group-hover:text-[#3B82F6] my-20 max-w-xs transition-colors duration-300">
                    {step.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
          {/* Highlight Text */}
<motion.div
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ delay: 0.2 }}
  className="mt-16 flex justify-center"
>
  <div className="bg-[#E6F4FF] border border-[#B6E0FE] rounded-xl p-16 max-w-3xl text-center shadow-sm">
    <p className="text-lg text-[#1F2937] font-inter leading-relaxed">
      With <span className="font-semibold text-[#2563EB]">Space Byte secure cloud storage</span>, education becomes more flexible, organized, and ready for the future of digital learning.
    </p>
  </div>
</motion.div>
        </div>
        
      </section>

      <section className="w-full  px-4 md:px-10 py-40 bg-white relative overflow-hidden">
        <div className="landing-container relative rounded-3xl min-h-[280px] md:min-h-[380px] flex items-center justify-center overflow-hidden">

          {/* Video Background */}
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/images/LandingImg/video.mp4"
            autoPlay
            loop
            muted
            playsInline
          />

          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Gradient Light */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-transparent to-blue-600/20" />

          {/* Content */}
          <div className="relative z-10 text-center max-w-4xl px-4">
            <h2 className="text-white text-3xl md:text-5xl font-semibold">
              Your Data, Your Control, Your Cloud
            </h2>

            <p className="text-white/80 text-base md:text-lg mt-20">
              A smarter cloud storage platform that gives you complete control over your data, file sharing, and digital privacy.

            </p>

            <div className="mt-20">
              <a href="/contact" className="btn bg-white text-black px-20 py-10 rounded-xl font-medium hover:scale-105 transition">
                <span>Contact Us</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      <section id="faq" className="bg-[#f5f8ff] relative overflow-hidden py-40 text-black lg:py-100">
        <div className="landing-container z-10">
          <div className="section-wrapper">
            <div className="section-inner">
              {/* LEFT PANEL */}
              <div className="left-panel">
                <div className="badge font-archivo">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="5" fill="#38bdf8" />
                  </svg>
                  SpaceByte Cloud
                </div>

                <h2 className="left-title font-archivo">
                  Best Cloud Storage <br />
                  Secure, Fast and  <br />
                  Reliable
                </h2>

                <p className="left-sub font-archivo">
                  Everything you need to store, share, and access your files anytime, anywhere.
                </p>

                <div className="illustration">
                  <div className="illus-card">
                    <div className="illus-row">
                      <div className="illus-icon ic-blue">☁️</div>
                      <div className="illus-text font-archivo">
                        <strong>My Storage</strong>
                        <span>18.3 GB used of 25 GB</span>
                        <div className="illus-bar">
                          <div className="illus-bar-fill" style={{ width: "73%" }} />
                        </div>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-sky">📁</div>
                      <div className="illus-text font-archivo">
                        <strong>Shared Folders</strong>
                        <span>12 folders · 3 collaborators</span>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-white">🔒</div>
                      <div className="illus-text">
                        <strong>End-to-End Encrypted</strong>
                        <span>All files secured in transit</span>
                      </div>
                    </div>

                    <div className="stats-row">
                      <div className="stat-chip">
                        <strong>25 GB</strong>
                        <span>Free Storage</span>
                      </div>
                      <div className="stat-chip">
                        <strong>99.9%</strong>
                        <span>Uptime</span>
                      </div>
                      <div className="stat-chip">
                        <strong>24/7</strong>
                        <span>Support</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="right-panel">
                {/* <p className="section-tag">Help Center</p> */}

                <h2 className="section-title">
                  Quick Reference Guide
                </h2>

                <p className="section-desc">
                  Find answers to the most common questions about SpaceByte Cloud Storage.
                </p>

                <div className="accordion">
                  {faqData.map((item, index) => (
                    <div
                      key={index}
                      className={`acc-item ${openIndex === index ? "open" : ""}`}
                    >
                      <button className="acc-header" onClick={() => toggle(index)}>
                        <div className="acc-num">
                          {String(index + 1).padStart(2, "0")}
                        </div>

                        <span className="acc-label">{item.question}</span>

                        <div className="acc-chevron">
                          <svg viewBox="0 0 24 24" fill="none">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </button>

                      <div className="acc-body">
                        <div
                          className="acc-body-inner"
                          dangerouslySetInnerHTML={{ __html: item.answer }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={ref}
        className="relative overflow-hidden bg-white py-[100px] max-[900px]:py-[72px]"
      >
        {/* Background Orbs (same as ::before/::after) */}
        <div className="pointer-events-none absolute -left-[180px] -top-[180px] h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,#dbeafe40,transparent_70%)]" />
        <div className="pointer-events-none absolute -bottom-[160px] -right-[160px] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,#ede9fe40,transparent_70%)]" />

        <div className="relative z-[1] mx-auto grid max-w-[1280px] grid-cols-2 items-stretch gap-[56px] px-[clamp(20px,5vw,72px)] max-[900px]:grid-cols-1 max-[900px]:gap-[40px]">
          {/* ───────────────── LEFT ───────────────── */}
          <div
            className={[
              "transition-all duration-700 ease-out",
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-7",
            ].join(" ")}
          >


            {/* Heading */}
            <h2 className="my-10 pt-6 md:pt-30 font-archivo text-[clamp(26px,3.2vw,46px)] font-extrabold leading-[1.15] tracking-[-0.025em] text-[#0f172a]">
              What Our {" "}
              <span className="bg-gradient-to-br font-archivo color-primary-gradient">
                Customers Say
              </span>{" "}

            </h2>

            {/* Text */}
            <p className="mb-10  max-w-[440px] font-archivo text-[clamp(14px,1.1vw,16px)] leading-[1.82] text-[#64748b]">
              See how SpaceByte delivers secure file storage, high-speed uploads, and advanced privacy protection. SpaceByte is a cloud storage platform trusted by individuals and businesses to safeguard their most important data. Read our verified customer reviews and testimonials below before making your secure cloud storage decision to see why users love us.
            </p>



            {/* Controls */}
            <div className="flex flex-wrap my-20  items-center gap-3">
              <button
                onClick={prev}
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowLeftIcon />
              </button>

              <button
                onClick={next}
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowRightIcon />
              </button>
            </div>

            {/* Dots */}
            <div className="ml-[6px] flex gap-[6px] my-16">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={[
                    "h-[8px] rounded-[4px] transition-all duration-300",
                    i === activeIndex ? "w-[26px] bg-[#2563eb]" : "w-[8px] bg-[#cbd5e1]",
                  ].join(" ")}
                />
              ))}
            </div>

            {/* Progress */}
            <div className="mt-5 h-[3px] max-w-[220px] overflow-hidden rounded bg-[#e2e8f0]">
              <div
                className="h-full rounded bg-gradient-to-r from-[#2563eb] to-[#7c3aed] transition-all duration-500"
                style={{ width: `${((activeIndex + 1) / total) * 100}%` }}
              />
            </div>
          </div>

          {/* ───────────────── RIGHT (SLIDER) ───────────────── */}
          <div
          className={[
            "w-full transition-all duration-700 ease-out delay-200",
            visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-7",
          ].join(" ")}
        >
          <div className="relative w-full overflow-hidden">
            {/* right fade */}
            <div className="pointer-events-none absolute right-0 top-0 z-[2] h-full w-[64px] bg-gradient-to-l from-white to-transparent" />

            <div
              className="flex items-stretch gap-6 transition-transform duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{
                transform: `translateX(-${offset}px)`,
              }}
            >
              {reviews.map((item, i) => (
                <ReviewCard key={i} item={item} isActive={i === activeIndex} />
              ))}
            </div>
          </div>
        </div>
      </div>
      </section>

      <section className="py-20 md:py-32 bg-gradient-to-br from-[#1E3A8A] via-[#3B82F6] to-[#60A5FA] relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 landing-container z-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center justify-center w-60 h-60 bg-white/20 backdrop-blur-sm rounded-2xl mb-12"
            >
              <Sparkles className="w-30 h-30 text-white" />
            </motion.div>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white"
            >
              Ready to Secure Your Data?
            </motion.h2>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-white/90 my-16 max-w-2xl mx-auto"
            >
              SpaceByte Cloud Storage is a comprehensive cloud storage solution that guarantees zero-knowledge encryption for businesses and individuals. Join thousands of users trusting our secure infrastructure to protect critical data, ensuring your files remain private, safe from ransomware, and accessible anytime.
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-10 justify-center items-center"
            >
              <a href="/register" className="group bg-white text-[#1E3A8A] px-16 py-14 rounded-xl font-semibold hover:bg-gray-100 transition-all hover:shadow-2xl hover:shadow-white/20 transform hover:-translate-y-1 flex items-center gap-2 btn">
                Get 25 GB FREE - No Card Needed
              </a>

            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-20 flex flex-wrap justify-center items-center gap-8 text-white/80 text-sm"
            >

            </motion.div>
          </motion.div>
        </div>
      </section>

      <footer className="Footer-section relative overflow-hidden bg-[#0F172A] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <div className="space-y-4 mr-12 padding-logo padding-top">
                <a href="https://spacebyte.in/" className="flex-shrink-0" aria-label="SpaceByte Homepage">
                  <img
                    src="/images/logo-light.png"
                    alt="Logo"
                    className="site-logo"
                  />
                </a>
                <div>
                  <p className="font-medium pt-8 pb-10">SPACEBYTE CLOUD STORAGE</p>
                  <p className="pb-10 pt-3 flex items-center gap-2">
                    311, Maruti Plaza, Sardar Patel Chowk, Krishnanagar, Ahmedabad, Gujarat,
                    India - 382345</p>
                  <p className="pb-10 pt-3 flex items-center gap-2">
                    Mo. +91 97257 71457
                  </p>
                </div>
                <div>
                  <p className="font-bold text-base pb-10 pt-3">FOLLOW US FOR "SPACEBYTE.IN"</p>
                  <div className="flex gap-4 mt-3 logg mb-10">
                    <ul className="flex space-x-16 pt-3 pr-10">
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.facebook.com/profile.php?id=61567307277313&mibextid=ZbWKwL">
                          <FacebookIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.instagram.com/spacebyte.in?igsh=MmZidnprMXZtbTho " aria-label="SpaceByte Instagram Profile">
                          <InstagramIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://youtube.com/@spacebytecloudstorage?si=lt2mfOM7kSaN3g6B" aria-label="SpaceByte YouTube Channel">
                          <YoutubeIcon className="text-white" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start ">Product</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#features" className="hover:text-white transition-colors text-start">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#Pricing" className="hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#security" className="hover:text-white transition-colors">
                    Security
                  </a>
                </li>
                <li>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Company</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/about-us" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                {/* <li>
                  <a href="/pages/articles" className="hover:text-white transition-colors">
                    Articles
                  </a>
                </li> */}
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Resources</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/pages/blog" className="hover:text-white transition-colors">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#faq" className="hover:text-white transition-colors">
                    FAQs
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                {/* <li>
                <a href="https://spacebyte.in/api-docs" className="hover:text-white transition-colors">
                  Developers
                </a>
              </li> */}
                <li>
                  <a href="/storage/sitemaps/sitemap-index.xml" className="hover:text-white transition-colors">
                    Sitemap
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div className='py-8 md:py-20 px-30'>
              <h3 className="font-semibold text-xl mb-10  text-start">Legal</h3>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="font-inter border-t border-t-primary/80 py-16 text-center mt-5">
            <p className="text-sm">
              Copyright © {new Date().getFullYear()}{' '}
              <a href="/login" className="color-primary-gradient">
                SpaceByte Cloud Storage
              </a>
              , All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
      </Fragment>
    );
  }

  function CalculateDialogTrigger() {
    const form = useForm<ContactPagePayload>({
      defaultValues: {
        message: '', // Initialize message field
      },
    });
    const [isOpen, setIsOpen] = useState(false);
    const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
    const submitForm = useSubmitContactForm(form);
    const { verify, isVerifying } = useRecaptcha('contact');

    return (
      <>
        <DialogTrigger type="modal" isOpen={isOpen} onOpenChange={setIsOpen}>
          <button className="btn w-full rounded-lg !text-white font-semibold
                  !bg-gradient-to-r !from-[#0072ff] !to-[#00c6ff]  shadow-lg
                  hover:shadow-xl transition">
            <span>Get Quote</span>
          </button>
          <Dialog className="w-full max-w-lg rounded-xl p-6">
            <DialogHeader
              className="font-archivo text-2xl font-semibold"
              showDivider
            >
              <Trans message="Storage Cost Calculator" />
            </DialogHeader>
            <DialogBody>
              <div className="font-inter flex items-center justify-center">
                Enter the storage size (whole number greater than **5 TB**) to
                calculate your monthly cost.
              </div>
              <Form
                form={form}
                className="mt-4 flex flex-col items-center text-center"
                onSubmit={async value => {
                  calculateCost(value);
                }}
              >
                <FormTextField
                  label={<Trans message="Enter Storage in TB" />}
                  name="tb"
                  required
                  type="number"
                  className="mb-6 w-full rounded-lg p-2"
                />
                <div className="text-red-500 text-md" id="errorMsg"></div>
                <div className="text-blue-500 text-md" id="resultOutput"></div>
                <div className="mt-6 flex w-full justify-between gap-4">
                  <button className="btn-warning mt-16" type="submit">
                    Calculate
                  </button>
                  <Button
                    color="positive"
                    variant="outline"
                    className="mt-16 rounded-lg"
                    type="button"
                    onClick={() => {
                      const resultOutput =
                        document.getElementById('resultOutput')?.textContent;
                      form.setValue('message', resultOutput || ''); // Set the value of message field
                      setIsOpen(false); // Close current dialog
                      setIsContactDialogOpen(true); // Open Contact Us dialog
                    }}
                  >
                    Contact Us
                  </Button>
                </div>
              </Form>
            </DialogBody>
          </Dialog>
        </DialogTrigger>

        {isContactDialogOpen && (
          <DialogTrigger
            type="modal"
            isOpen={isContactDialogOpen}
            onOpenChange={setIsContactDialogOpen}
          >
            <Dialog className="w-full max-w-lg rounded-xl p-6">
              <DialogHeader
                className="font-archivo text-2xl font-semibold"
                showDivider
              >
                <Trans message="Contact Us" />
              </DialogHeader>
              <DialogBody>
                {submitForm.isPending ? (
                  <FullPageLoader />
                ) : (
                  <Form
                    form={form}
                    className="flex flex-col gap-4"
                    onSubmit={async value => {
                      const isValid = await verify();
                      if (isValid) {
                        submitForm.mutate(value, {
                          onSuccess: () => {
                            setIsContactDialogOpen(false); // Close the dialog on success
                          },
                        });
                      }
                    }}
                  >
                    <FormTextField
                      label={<Trans message="Name" />}
                      name="name"
                      required
                      className="rounded-lg p-2"
                    />
                    <FormTextField
                      label={<Trans message="Email" />}
                      name="email"
                      required
                      type="email"
                      className="rounded-lg p-2"
                    />
                    <FormTextField
                      label={<Trans message="Mobile Number" />}
                      name="mobileNumber"
                      required
                      type="number"
                      className="rounded-lg p-2"
                    />
                    <FormTextField
                      label={<Trans message="Message" />}
                      name="message"
                      required
                      inputElementType="textarea"
                      className="rounded-lg p-2"
                      rows={5}
                    />

                    <div className="mt-4 flex w-full justify-between gap-4">
                      <Button
                        variant="flat"
                        onClick={() => setIsContactDialogOpen(false)}
                        className="rounded-lg px-4 py-2 text-lg"
                      >
                        <Trans message="Back" />
                      </Button>
                      <Button
                        type="submit"
                        variant="flat"
                        color="primary"
                        className="rounded-lg px-4 py-2 text-lg"
                      >
                        <Trans message="Send Message" />
                      </Button>
                    </div>
                  </Form>
                )}
              </DialogBody>
            </Dialog>
          </DialogTrigger>
        )}
      </>
    );
  }

  function calculateCost({ tb }: any) {
    const tbInput: any = tb;
    const errorMsg: any = document.getElementById('errorMsg');
    const resultOutput: any = document.getElementById('resultOutput');

    // Reset messages
    errorMsg.textContent = '';
    resultOutput.textContent = '';

    // Validation: Ensure input is a positive integer and greater than 5
    if (!/^\d+$/.test(tbInput)) {
      errorMsg.textContent = 'Error: Storage must be a whole number!';
      return;
    }

    const tbValue = parseInt(tbInput, 10);

    if (tbValue <= 5) {
      errorMsg.textContent = 'Error: Storage must be greater than 5 TB!';
      return;
    }

    // Conversion and cost calculation
    const gbStorage = tbValue * 1024; // Convert TB to GB
    const cost = gbStorage * 0.15; // Calculate cost

    // Display result
    resultOutput.innerHTML = `
        Storage: <strong>${tbValue.toLocaleString()} TB</strong><br>
        Total Cost: <strong>₹${cost.toFixed(2)}/month</strong><br>
        GST and Convenience Fee Extra**
    `;
  }

  function ReviewCard({
    item,
    isActive,
  }: {
    item: any;
    isActive: boolean;
  }) {
    const { name, role, avatar, review, accent, light, rating } = item;
    
    // Extract the first letter of the name
    const initial = name?.charAt(0).toUpperCase() || "U";

    return (
      <div
        className={[
          "relative flex-shrink-0 overflow-hidden m-10 rounded-[28px] border-[1.5px] p-[clamp(24px,3.5vw,42px)] transition-all duration-500 flex flex-col justify-between",
          isActive ? "translate-y-[-4px] scale-[1.015]" : "scale-[0.97]",
        ].join(" ")}
        style={{
          minWidth: "clamp(270px, 42vw, 450px)",
          maxWidth: "clamp(270px, 42vw, 450px)",
          background: isActive
            ? `linear-gradient(150deg, ${light} 0%, white 100%)`
            : `linear-gradient(150deg, ${light}88 0%, #f8fafc 100%)`,
          borderColor: isActive ? `${accent}50` : "#e2e8f0",
          boxShadow: isActive
            ? `0 24px 60px ${accent}1a, 0 4px 16px ${accent}10`
            : "0 2px 12px rgba(0,0,0,0.05)",
          opacity: isActive ? 1 : 0.68,
        }}
      >
        <div>
          {/* Rating Section */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <span key={i} style={{ color: i < rating ? accent : "#cbd5e1" }} className="text-[18px]">
                  ★
                </span>
              ))}
            </div>
            {/* Subtle Quote Mark */}
            <span className="font-serif text-[60px] leading-none opacity-10 absolute right-8 top-6" style={{ color: accent }}>
              “
            </span>
          </div>

          <p className="font-archivo text-[clamp(14px,1.15vw,16px)] font-medium leading-[1.7] mb-8 text-[#334155]">
            "{review}"
          </p>
        </div>

        <div className="flex items-center gap-10">
          {/* AVATAR / INITIAL BOX */}
          <div 
            className="flex h-50 w-50 shrink-0 items-center justify-center rounded-full border-2 font-archivo text-[20px] font-bold"
            style={{ 
              borderColor: `${accent}40`,
              background: isActive ? `${accent}15` : "#f1f5f9",
              color: accent 
            }}
          >
            {avatar ? (
              <img src={avatar} alt={name} className="h-full w-full object-cover rounded-full" />
            ) : (
              <span>{initial}</span> 
            )}
          </div>

          <div className="overflow-hidden ">
            <h4 className="truncate font-archivo text-[17px] font-bold text-[#0f172a] leading-tight">
              {name}
            </h4>
            <p className="truncate text-[13px] font-semibold" style={{ color: accent }}>
              {role}
            </p>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 h-[3px] rounded-bl-[28px] transition-all duration-[650ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{
            width: isActive ? "100%" : "0%",
            background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
          }}
        />
      </div>
    );
  }